import { useState } from "react";
import { login, me } from "../../api/auth.api";
import { redeemTeamCode } from "../../api/payments.api";
import useAuthStore from "../../store/auth.store";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { CircleCheckBig, UserRoundPlus, CircleArrowRight, Smartphone, Ticket } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [teamCode, setTeamCode] = useState("");
  const [showTeamCode, setShowTeamCode] = useState(false);
  const [error, setError] = useState(null);
  const [teamCodeNote, setTeamCodeNote] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setTeamCodeNote(null);

    try {
      const loginRes = await login(username, password);

      // Session Auth: Cookie is set by browser automatically if SameSite/Secure allows.

      // If login returns the user data directly, use it
      // Otherwise fetch from /me endpoint
      const userData = loginRes.data.user || loginRes.data || (await me()).data;
      setAuth(userData);

      // Optional team code — redeem right after login so the user lands
      // on /dashboard already on the granted plan. Failures don't block
      // the navigation; we just surface a non-fatal message so the user
      // can retry from the account page. Reasoning: the user is already
      // authenticated; refusing to send them on because their code is
      // wrong would be worse UX than letting them in to fix it later.
      const code = (teamCode || "").trim().toUpperCase();
      if (code) {
        try {
          await redeemTeamCode(code);
          // Success — navigate normally; the dashboard will read the
          // new ACTIVE sub on its first render.
        } catch (codeErr) {
          const codeStatus = codeErr?.response?.status;
          const codeDetail = codeErr?.response?.data?.detail || "Team code couldn't be redeemed.";
          let msg = codeDetail;
          if (codeStatus === 404) msg = "Team code not recognised — check the spelling.";
          else if (codeStatus === 409) msg = "You've already redeemed this code on this account.";
          else if (codeStatus === 410) msg = `That code is no longer valid (${codeDetail}).`;
          // Stash a session note so the next page can surface it (the
          // user has navigated away by the time they see it).
          try { sessionStorage.setItem("team_code_redeem_error", msg); } catch {}
        }
      }

      // Post-login routing:
      //   STANDBY    → /maintenance (pre-launch lockout)
      //   superuser  → /admin (Owner Console — founder lives here)
      //   otherwise  → /dashboard
      const postLoginPath =
        userData?.status === "STANDBY" ? "/maintenance"
        : userData?.is_superuser       ? "/admin"
        :                                "/dashboard";
      navigate(postLoginPath);
    } catch (err) {
      console.error("Login Error:", err);
      // Surface specific failure modes when the backend hints at them.
      // For security reasons, most identity APIs return a generic
      // "invalid credentials" — we surface the same to avoid email
      // enumeration. But common transient errors (rate-limit, network)
      // deserve specific copy so the user knows the password is fine
      // and just to try again.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.response?.data?.message;
      if (status === 429) {
        setError("Too many sign-in attempts. Try again in a minute, or use Forgot password.");
      } else if (status === 423 || (typeof detail === "string" && /lock/i.test(detail))) {
        setError("This account is temporarily locked after several failed attempts. Try again in 15 minutes or reset your password.");
      } else if (!navigator.onLine || err?.code === "ERR_NETWORK") {
        setError("Can't reach the server — check your internet connection.");
      } else if (detail && /not.*verified/i.test(detail)) {
        setError("Email not verified yet. Check your inbox (and spam) for the verification link.");
      } else {
        setError(detail || "Invalid username/email or password.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-bg-main text-text-primary flex flex-col items-center justify-center px-6 py-10">
      {/* 2026-06-05 (v2) — logo image removed per founder follow-up.
          Wordmark-only sign-in header. */}
      <Link to="/" className="flex flex-col items-center mb-8">
        <span className="font-bold text-2xl tracking-tight">
          Zen<span className="text-button-primary">cricket</span>data
        </span>
      </Link>

      <form
        onSubmit={handleSubmit}
        data-testid="login-form"
        className="w-full max-w-md bg-bg-primary/70 backdrop-blur-xl border border-border-default/80 rounded-3xl p-7 sm:p-10 shadow-2xl"
      >
        <div className="text-center mb-8">
          <h1 className="text-2xl font-extrabold tracking-tight">Welcome back</h1>
          <p className="text-text-secondary text-sm mt-1">Sign in to your scoring console</p>
        </div>

        <label className="block mb-4">
          <span className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1.5">Email, mobile, or username</span>
          <input
            data-testid="login-username"
            className="w-full bg-bg-secondary border border-border-default rounded-full py-3 px-4 text-sm text-text-primary placeholder-text-secondary/60 focus:outline-none focus:border-button-primary transition-colors"
            placeholder="you@example.com  ·  +91 98765 43210  ·  yourname"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </label>

        <label className="block mb-2">
          <div className="flex items-center justify-between mb-1.5">
            <span className="block text-[10px] uppercase tracking-wider text-text-secondary">Password</span>
            <Link to="/forgot-password" className="text-[11px] text-button-primary hover:brightness-110 transition-all">
              Forgot password?
            </Link>
          </div>
          <input
            type="password"
            data-testid="login-password"
            className="w-full bg-bg-secondary border border-border-default rounded-full py-3 px-4 text-sm text-text-primary placeholder-text-secondary/60 focus:outline-none focus:border-button-primary transition-colors"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>

        {/* Team / coupon code — optional. Hidden behind a toggle so the
            login form stays uncluttered for regular customers. Internal
            testers + invited users expand it, paste their code, and the
            backend redeems automatically after auth. */}
        <div className="mt-4">
          {!showTeamCode ? (
            <button
              type="button"
              onClick={() => setShowTeamCode(true)}
              className="w-full flex items-center justify-center gap-2 text-[11px] text-text-secondary hover:text-text-primary py-2 transition-colors"
            >
              <Ticket className="w-3.5 h-3.5" />
              Have a team or coupon code?
            </button>
          ) : (
            <label className="block">
              <div className="flex items-center justify-between mb-1.5">
                <span className="block text-[10px] uppercase tracking-wider text-text-secondary">Team code (optional)</span>
                <button
                  type="button"
                  onClick={() => { setShowTeamCode(false); setTeamCode(""); }}
                  className="text-[10px] text-text-secondary hover:text-text-primary"
                >
                  Hide
                </button>
              </div>
              <input
                type="text"
                className="w-full bg-bg-secondary border border-border-default rounded-full py-3 px-4 text-sm text-text-primary placeholder-text-secondary/60 focus:outline-none focus:border-button-primary transition-colors font-mono uppercase"
                placeholder="TEAM-XXXXXXXX"
                value={teamCode}
                onChange={(e) => setTeamCode(e.target.value)}
                spellCheck={false}
                autoComplete="off"
                autoCapitalize="characters"
              />
              <p className="text-[10px] text-text-secondary mt-1.5">
                Redeemed automatically after sign-in. Invalid codes won't
                block your login.
              </p>
            </label>
          )}
        </div>

        {error && (
          <div className="bg-button-primary/10 border border-button-primary/30 rounded-lg px-3.5 py-2.5 text-sm text-button-primary mt-3">
            {error}
          </div>
        )}

        <div className="md:hidden mt-4 flex items-start gap-2 text-[11px] text-text-secondary bg-bg-secondary/50 border border-border-default rounded-lg px-3 py-2">
          <Smartphone className="w-3.5 h-3.5 shrink-0 mt-0.5 text-text-secondary/80" />
          <span>
            <strong className="text-text-primary">Pro tip:</strong> Add to Home Screen for fullscreen mode (Share → Add to Home Screen on iOS, or browser menu → Install on Chrome).
          </span>
        </div>

        <button
          disabled={loading}
          data-testid="login-submit"
          className="mt-6 w-full bg-button-primary hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-full transition-all flex items-center justify-center gap-2"
        >
          {loading ? "Signing in…" : "Sign in"}
          {!loading && <CircleArrowRight className="w-4" />}
        </button>

        {/* Signups disabled 2026-06-04 — restore the divider + "Create one" link to re-enable. */}
      </form>
    </div>
  );
}
